import {
  CUSTOM_FIELD_FILTER_PREFIX,
  CUSTOMER_SITUATIONS,
  CUSTOMER_SORTS,
  CUSTOMER_TYPES,
  DEFAULT_PAGE_SIZE,
  NO_OWNER,
  type CustomerListQuery,
  type CustomerSituation,
  type CustomerSort,
  type CustomerType,
} from '../contract';

/**
 * The state of the customers list in the URL (spec 004, §7.2), in English:
 * `?q=&type=&situation=&owner=&unit=&tag=&archived=true&sort=&page=&cf.<key>=`. Reloading
 * or sharing the link shows the same list. `owner` takes a membership id, `none` (without
 * owner) or `me` (the signed-in member — the link of the home block).
 */
export interface CustomerListSearch {
  q?: string;
  type?: CustomerType;
  situation?: CustomerSituation;
  owner?: string;
  unit?: string;
  tag?: string;
  archived?: true;
  sort?: CustomerSort;
  page?: number;
  /** Custom field filters, `cf.<key>` → value. */
  [customField: `cf.${string}`]: string | undefined;
}

export const OWNER_ME = 'me';

const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);

/** The router parses `?q=123` into a number and `?archived=true` into a boolean: back to text. */
function text(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() === '' ? undefined : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

/** Reads the list state from the URL; anything malformed is dropped (never an error page). */
export function parseCustomerListSearch(raw: Record<string, unknown>): CustomerListSearch {
  const search: CustomerListSearch = {};
  const q = text(raw['q']);
  if (q) search.q = q;
  if (isOneOf(CUSTOMER_TYPES, raw['type'])) search.type = raw['type'];
  if (isOneOf(CUSTOMER_SITUATIONS, raw['situation'])) search.situation = raw['situation'];
  const owner = text(raw['owner']);
  if (owner) search.owner = owner;
  const unit = text(raw['unit']);
  if (unit) search.unit = unit;
  const tag = text(raw['tag']);
  if (tag) search.tag = tag;
  if (raw['archived'] === true || raw['archived'] === 'true') search.archived = true;
  if (isOneOf(CUSTOMER_SORTS, raw['sort'])) search.sort = raw['sort'];
  const page = Number(raw['page']);
  if (Number.isInteger(page) && page > 1) search.page = page;
  for (const [key, value] of Object.entries(raw)) {
    if (!key.startsWith(CUSTOM_FIELD_FILTER_PREFIX)) continue;
    const filter = text(value);
    if (filter) search[key as `cf.${string}`] = filter;
  }
  return search;
}

/** The custom field filters of the state, `{ key: value }`. */
export function customFieldFilters(search: CustomerListSearch): Record<string, string> {
  return Object.fromEntries(
    Object.entries(search).flatMap(([key, value]) =>
      key.startsWith(CUSTOM_FIELD_FILTER_PREFIX) && typeof value === 'string'
        ? [[key.slice(CUSTOM_FIELD_FILTER_PREFIX.length), value]]
        : [],
    ),
  );
}

/** The API query for the state; `me` becomes the caller's membership. */
export function toCustomerListQuery(
  search: CustomerListSearch,
  membershipId: string | undefined,
): CustomerListQuery {
  const owner = search.owner === OWNER_ME ? membershipId : search.owner;
  const customFields = customFieldFilters(search);
  return {
    ...(search.q ? { q: search.q } : {}),
    ...(search.type ? { type: search.type } : {}),
    ...(search.situation ? { situation: search.situation } : {}),
    ...(owner ? { ownerMembershipId: owner } : {}),
    ...(search.unit ? { unitId: search.unit } : {}),
    ...(search.tag ? { tag: search.tag } : {}),
    ...(search.archived ? { archived: true } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    page: search.page ?? 1,
    pageSize: DEFAULT_PAGE_SIZE,
    ...(Object.keys(customFields).length > 0 ? { customFields } : {}),
  };
}

/** Whether anything narrows the list (search, a filter or the archived view). */
export function hasActiveFilters(search: CustomerListSearch): boolean {
  return Object.entries(search).some(
    ([key, value]) => value !== undefined && key !== 'sort' && key !== 'page',
  );
}

/** The state without search, filters and page (the sort stays). */
export function clearedSearch(search: CustomerListSearch): CustomerListSearch {
  return search.sort ? { sort: search.sort } : {};
}

/** Changes some filters: the page goes back to the first one. */
export function withFilters(
  search: CustomerListSearch,
  changes: Partial<CustomerListSearch>,
): CustomerListSearch {
  const next: CustomerListSearch = { ...search, ...changes };
  delete next.page;
  for (const [key, value] of Object.entries(next)) {
    if (value === undefined || value === '') delete next[key as keyof CustomerListSearch];
  }
  return next;
}

/** The fixed filters of the filters panel, in the order their chips show (not the search box). */
export const PANEL_FILTERS = ['type', 'situation', 'owner', 'unit', 'tag', 'archived'] as const;

/**
 * One filter of the panel: a fixed one or a custom field (`cf.<key>`), which follow the fixed
 * ones before "Arquivados".
 */
export type PanelFilter = (typeof PANEL_FILTERS)[number] | `cf.${string}`;

/** The panel filters that are on, in chip order ("Arquivados" last). */
export function activePanelFilters(search: CustomerListSearch): PanelFilter[] {
  const fixed = PANEL_FILTERS.filter((key) => key !== 'archived' && search[key] !== undefined);
  const custom = Object.keys(customFieldFilters(search)).map(
    (key) => `${CUSTOM_FIELD_FILTER_PREFIX}${key}` as const,
  );
  return [...fixed, ...custom, ...(search.archived ? (['archived'] as const) : [])];
}

/** Turns one panel filter off (the page goes back to the first one). */
export function withoutFilter(search: CustomerListSearch, key: PanelFilter): CustomerListSearch {
  return withFilters(search, { [key]: undefined });
}

/** The state without the panel filters: the search text and the sort stay. */
export function clearedPanelFilters(search: CustomerListSearch): CustomerListSearch {
  const next = clearedSearch(search);
  if (search.q) next.q = search.q;
  return next;
}

export { NO_OWNER };
